import { useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";

import { parseExpertResult } from "shared/lib";
import { type Conversation, Message } from "shared/apiClient";
import { AccountType } from "shared/types";
import { EventTypes } from "@/types/events";
import { sortByDate } from "@/lib/utils";
import {
  accountVar,
  isLoadingVar,
  messagesVar,
  noticeboardDataVar,
  tabNotificationsVar,
  Tabs,
  tabSelectedVar,
} from "@/variables/globalVar";

import { useGetConversationMessages } from "shared/apiHooks";
import {
  useExistingLandUses,
  useProjectDetails,
  useProposedLandUses,
} from "shared/hooks";
import { useAccount, useShouldRenderSideBySide } from "@/hooks";
import { type SubscriptionDescriptors, useChannel } from "shared/channel";

import { Wizard } from "@/components/wizard";
import { AnalysisTabsContainer } from "@/components/analysisLayout/analysisTabs";

export function MyLotPage() {
  const wizardContainerRef = useRef<HTMLDivElement>(null);

  const { channel } = useChannel();

  const { application, conversation } = useAccount();
  const [, setAccountVar] = useAtom(accountVar);
  const [messages, setMessagesVar] = useAtom(messagesVar);

  const [tabSelected, setTabSelectedVar] = useAtom(tabSelectedVar);
  const setTabNotifications = useSetAtom(tabNotificationsVar);

  const setIsLoadingVar = useSetAtom(isLoadingVar);

  const [noticeboardData, setNoticeboardDataVar] = useAtom(noticeboardDataVar);

  const shouldShowSideBySide = useShouldRenderSideBySide();

  const existingLandUses = useExistingLandUses(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const proposedLandUses = useProposedLandUses(
    conversation,
    noticeboardData,
    "enquiry",
  );

  const { content: projectDetails } = useProjectDetails(
    conversation,
    noticeboardData,
    "enquiry",
  );

  useEffect(() => {
    const handleScrollToTop: EventListener = (_event) => {
      if (wizardContainerRef.current) {
        wizardContainerRef.current.scrollTop = 0;
      }
    };

    document.addEventListener(EventTypes.ScrollToTop, handleScrollToTop);

    return () => {
      document.removeEventListener(EventTypes.ScrollToTop, handleScrollToTop);
    };
  }, []);

  useEffect(() => {
    if (shouldShowSideBySide) {
      setTabSelectedVar(Tabs.Permissibility);
    }
  }, [setTabSelectedVar, shouldShowSideBySide]);

  const {
    data: conversationMessagesResponse,
    error: conversationMessagesError,
    isLoading: isLoadingConversationMessages,
    refetch: refetchConversationMessages,
  } = useGetConversationMessages(
    {
      path: {
        application_id: application?.id || "",
        conversation_id: conversation?.id || "",
      },
      query: {
        page: 1,
        page_size: 9999,
      },
    },
    {
      enabled: !!conversation,
    },
  );

  useEffect(() => {
    console.log(
      "<MyLotPage> conversationMessagesResponse.data",
      conversationMessagesResponse?.data,
    );
    const results = conversationMessagesResponse?.data.results || [];

    const sortedMessages = results.sort(sortByDate);

    const latestMessage = sortedMessages[sortedMessages.length - 1];
    if (latestMessage?.sender_account_type === AccountType.ASSISTANT) {
      setIsLoadingVar(false);
    }

    setMessagesVar(() => {
      if (sortedMessages.length > 0) {
        return sortedMessages as Conversation["messages"];
      }
      return [];
    });
  }, [conversationMessagesResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const openedSubscriptionDescriptor: SubscriptionDescriptors = {
      event_type: "open",
      callback: () => {
        console.log("<MyLotPage> channel connected.");
        refetchConversationMessages();
      },
    };
    channel?.subscribe(openedSubscriptionDescriptor);

    const conversationSubscriptionDescriptor: SubscriptionDescriptors = {
      event_type: "CONVERSATION",
      callback: (event) => {
        const innerEvent = event.data;
        switch (innerEvent.event_type) {
          case "MESSAGE_CREATED":
            console.log(
              "<MyLotPage> message created",
              innerEvent,
              conversation?.id,
            );
            if (conversation?.id === innerEvent.data.conversation_id) {
              const alreadyExists = messages?.some(
                (message) => message.id === innerEvent.data.id,
              );
              if (!alreadyExists) {
                setMessagesVar((prev) => {
                  return [...(prev || []), innerEvent.data as Message];
                });
                if (
                  innerEvent.data.sender_account_type === AccountType.ASSISTANT
                ) {
                  setIsLoadingVar(false);
                }
              }
            }
            break;
          case "PHASE_UPDATED":
            if (conversation?.id === innerEvent.data.conversation_id) {
              setAccountVar((draft) => {
                const currentApplication = draft?.current_application;
                if (currentApplication?.current_conversation) {
                  currentApplication.current_conversation.phase = innerEvent
                    .data.phase as Conversation["phase"];
                }
              });
            }
            break;
        }
      },
    };
    channel?.subscribe(conversationSubscriptionDescriptor);

    const noticeboardSubscriptionDescriptor: SubscriptionDescriptors = {
      event_type: "NOTICEBOARD",
      callback: (event) => {
        const innerEvent = event.data;
        switch (innerEvent.event_type) {
          case "EXPERT_STARTED":
            // if (conversation?.id === innerEvent.data.conversation_id) {
            //   setNoticeboardDataVar((draft) => {
            //     draft[innerEvent.data.expert_name] = parseExpertResult(
            //       innerEvent.data,
            //     );
            //   });
            // }
            break;
          case "NOTICEBOARD_UPDATED":
            if (conversation?.id === innerEvent.data.conversation_id) {
              setNoticeboardDataVar((draft) => {
                draft[innerEvent.data.expert_name] = parseExpertResult(
                  innerEvent.data,
                );
              });
            }
            break;
        }
      },
    };
    channel?.subscribe(noticeboardSubscriptionDescriptor);

    return () => {
      channel?.unsubscribe(openedSubscriptionDescriptor);
      channel?.unsubscribe(conversationSubscriptionDescriptor);
      channel?.unsubscribe(noticeboardSubscriptionDescriptor);
    };
  }, [channel, conversation, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("<MyLotPage> noticeboardData updated:", noticeboardData);
  }, [noticeboardData]);

  useEffect(() => {
    if (conversation?.address_data) {
      if (tabSelected !== Tabs.Lot) {
        setTabNotifications((prev) => (prev.lot ? prev : { ...prev, lot: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversation?.address_data?.lot_size_sqm,
    conversation?.address_data?.unstructured_address,
    conversation?.address_data?.overlays,
    conversation?.address_data?.zones,
  ]);

  useEffect(() => {
    if (existingLandUses && existingLandUses.length > 0) {
      if (tabSelected !== Tabs.Lot) {
        setTabNotifications((prev) =>
          prev.proposal ? prev : { ...prev, proposal: true },
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?.id, noticeboardData]);

  useEffect(() => {
    if (
      proposedLandUses.length > 0 &&
      tabSelected !== Tabs.Proposal &&
      messages &&
      messages.filter(
        (message) => message.sender_account_type === AccountType.ASSISTANT,
      ).length >= 4
    ) {
      setTabNotifications((prev) =>
        prev.proposal ? prev : { ...prev, proposal: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedLandUses]);

  useEffect(() => {
    if (projectDetails) {
      if (tabSelected !== Tabs.Proposal) {
        if (
          messages &&
          messages.filter(
            (message) => message?.sender_account_type === AccountType.ASSISTANT,
          ).length >= 4
        ) {
          setTabNotifications((prev) =>
            prev.proposal ? prev : { ...prev, proposal: true },
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetails]);

  useEffect(() => {
    console.log("<MyLotPage> convPhase:", conversation?.phase);
    if (
      [
        "ready_for_assessment",
        "report_ready",
        "amendment",
        "saved_report",
        "sent_to_council",
      ].includes(conversation?.phase || "") &&
      tabSelected !== Tabs.Permissibility
    ) {
      setTabSelectedVar(Tabs.Permissibility);
    }
  }, [conversation?.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative flex min-h-full grow flex-col">
      <section
        className="flex grow flex-col overflow-y-auto"
        ref={wizardContainerRef}
      >
        <Wizard />
      </section>

      {!shouldShowSideBySide &&
      [
        "ready_for_assessment",
        "report_ready",
        "amendment",
        "saved_report",
        "sent_to_council",
      ].includes(conversation?.phase || "") ? (
        <AnalysisTabsContainer conversation={conversation} />
      ) : null}
    </div>
  );
}
